/**
 * API service functions for Overmatch backend.
 * Organized by domain for easy maintenance.
 */

import { apiClient } from './api'

// ============================================================================
// Type Definitions
// ============================================================================

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: 'leader' | 'pm' | 'reviewer' | 'legal' | 'contracting'
}

export interface Program {
  id: number
  name: string
  description: string
  objectives: string
  owner: User
  technology: number
  technology_name: string
  selected_technologies?: number[]
  status: 'planning' | 'active' | 'evaluation' | 'field_test' | 'awarded' | 'closed'
  status_display: string
  created_at: string
  updated_at: string
  submissions_count: number
  reviews_completed: number
  reviews_total: number
  is_starred: boolean
  // Frontend compatibility fields
  rfpNumber: string
  category: string
  submittedAt: string
  submissionsCount: number
  reviewsCompleted: number
  reviewsTotal: number
  primaryContact: string
}

export interface Vendor {
  id: number
  name: string
  website: string
  website_display: string
  sam_gov_id: string
  duns_number: string
  cage_code: string
  primary_contact_name: string
  primary_contact_email: string
  primary_contact_phone: string
  city: string
  state: string
  country: string
  created_at: string
  updated_at: string
  created_by_name: string | null
  team_size?: number
  total_contracts?: number
  total_contract_value?: number
  // Extended profile fields
  description?: string
  uei?: string
  address?: string
  headquarters_location?: string
  founded_year?: number | null
  employee_count?: number | null
  employee_count_range?: string
  entity_structure?: string
  organization_structure?: string
  primary_naics?: string
  naics_codes?: any
  psc_codes?: any
  annual_revenue_range?: string
  congressional_district?: string
  vendor_type?: string
  incorporation_date?: string | null
  incorporation_state?: string
  // Compliance fields
  is_small_business?: boolean | null
  is_8a_certified?: boolean | null
  is_8a_program_year?: number | null
  is_8a_tribal_owned?: boolean | null
  is_veteran_owned?: boolean | null
  is_woman_owned?: boolean | null
  is_hubzone?: boolean | null
  hubzone_certification_expires?: string | null
  sdvosb_certification_expires?: string | null
  debarment_status?: string
  has_foreign_ownership?: boolean | null
  foci_details?: string
  section_889_compliant?: boolean | null
  has_active_proceedings?: boolean | null
  company_security_level?: string
  highest_employee_security_level?: string
  consortium_memberships?: any
  is_disaster_response_contractor?: boolean | null
  sam_registration_date?: string | null
  sam_last_update_date?: string | null
  sam_expiration_date?: string | null
  risk_rating?: string
  risk_rationale?: string
  // Nested relations (detail view)
  products?: VendorProduct[]
  team_members?: TeamMember[]
  contracts?: any[]
}

export interface TeamMember {
  id: number
  vendor: number
  name: string
  role: string
  title: string
  email: string
  phone: string
  linkedin_url: string
  bio: string
  is_primary_contact: boolean
  is_founder: boolean
  is_c_suite: boolean
  security_clearance: string
  citizenship_status: string
  clearance_expiry_date: string | null
  technical_certifications: string[]
  extracted_from_document: boolean
  added_at: string
}

export interface VendorProduct {
  id: number
  vendor: number
  vendor_name: string
  name: string
  product_type: string
  description: string
  search_stub: string
  product_url: string
  category: string
  manufacturing_status: string
  supply_chain_notes: string
  technical_specs: any
  is_active: boolean
  availability: string
  images: ProductImage[]
  primary_image: ProductImage | null
  technology_ids: number[]
  created_at: string
  updated_at: string
}

export interface ProductImage {
  id: number
  product: number
  image_url: string | null
  source_url: string | null
  s3_key: string | null
  is_primary: boolean
  order: number
  width: number | null
  height: number | null
  file_size: number | null
  format: string | null
  created_at: string
}

export interface VendorOpportunity {
  id: number
  program_id: number
  program_name: string | null
  office_name: string | null
  status: string
  recommendation_type: string
  overall_score: number | null
  product_id: number | null
  product_name: string | null
  recommended_at: string | null
  status_updated_at: string | null
}

export interface Observation {
  id: number
  entity_type: 'product' | 'vendor'
  spec: number
  spec_name?: string
  spec_type?: string
  spec_unit?: string
  spec_priority?: number
  vendor?: number
  product?: number
  value_numeric: number | null
  value_boolean: boolean | null
  value_text: string | null
  confidence: number
  source_type: number
  source_type_key?: string
  source_display_name?: string
  source_badge_color?: string
  source_priority?: number
  observed_date: string
  created_at: string
  created_by?: number
  created_by_name?: string
}

export interface Spec {
  id: number
  name: string
  spec_type: 'numeric' | 'boolean' | 'categorical' | 'text'
  category: string
  unit: string
  description: string
  higher_is_better: boolean
  technology: number | null
}

export interface SelectionStage {
  id: number
  acquisition_plan: number
  name: string
  description: string
  order: number
  duration_days: number
  start_offset_days: number
  planned_start_date: string | null
  actual_start_date: string | null
  actual_end_date: string | null
  status: 'not_started' | 'in_progress' | 'completed' | 'delayed'
  notes: string
  created_at: string
  updated_at: string
}

export interface VendorRecommendation {
  recommendation_id: number
  vendor: {
    id: number
    name: string
    website: string
    headquarters_location: string
    employee_count: number | null
  }
  product: {
    id: number | null
    name: string
    product_type: string
    description: string
  }
  technical_fit: {
    overall_score: number
    strengths: string[]
    gaps: string[]
  }
  sole_source: {
    tier: number | null
    rationale: string | null
    is_eligible: boolean
  }
  scorecards: {
    manufacturing_readiness: number
    vc_metrics: number
    dod_partnership: number
  }
  overall_recommendation_score: number
  status: string
  selection_stage_id: number | null
  selection_stage_name: string | null
  selection_stage_order: number | null
  recommended_at: string | null
}

export interface Submission {
  id: number
  vendor: Vendor
  program: number
  program_name: string
  technology: number
  technology_name: string
  status: 'invited' | 'draft' | 'submitted' | 'under_review' | 'scored' | 'selected' | 'rejected'
  status_display: string
  selection_stage: number | null
  selection_stage_name: string | null
  selection_stage_order: number | null
  submission_stage: string  // DEPRECATED: use selection_stage_name
  submission_contact_name: string
  submission_contact_email: string
  submission_contact_phone: string
  submission_date: string
  last_updated: string
  created_at: string
  notes: string
  metadata: Record<string, any>
  technical_score: number | null
  manufacturing_score: number | null
  vc_score: number | null
  dod_score: number | null
}

export interface Evaluation {
  id: number
  submission: number
  scorecard: number
  scorecard_type: string
  evaluator: number | null
  evaluator_name: string | null
  evaluation_type: string
  evaluation_type_display: string
  status: 'not_started' | 'in_progress' | 'completed'
  status_display: string
  overall_score: string | null
  completion_percentage: number
  notes: string
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface Technology {
  id: number
  name: string
  description: string
  parent: number | null
  parent_name: string | null
  full_path: string
  depth: number
  children_count?: number
  created_at: string
  updated_at: string
}

export interface ProgramStats {
  total_submissions: number
  submissions_by_status: Record<string, number>
  total_evaluations: number
  evaluations_completed: number
  total_documents: number
  total_stakeholders: number
}

// ============================================================================
// Programs API
// ============================================================================

export const programsAPI = {
  /**
   * Get all programs (filtered by user permissions)
   */
  async list(params?: { status?: string; search?: string; ordering?: string }): Promise<Program[]> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.ordering) queryParams.append('ordering', params.ordering)

    const endpoint = `/api/programs/${queryParams.toString() ? `?${queryParams}` : ''}`
    // API client automatically extracts results array from paginated responses
    return apiClient.get<Program[]>(endpoint)
  },

  /**
   * Get a specific program by ID
   */
  async get(id: number): Promise<Program> {
    return apiClient.get<Program>(`/api/programs/${id}/`)
  },

  /**
   * Get submissions for a program
   */
  async getSubmissions(id: number): Promise<Submission[]> {
    return apiClient.get<Submission[]>(`/api/programs/${id}/submissions/`)
  },

  /**
   * Get statistics for a program
   */
  async getStats(id: number): Promise<ProgramStats> {
    return apiClient.get<ProgramStats>(`/api/programs/${id}/stats/`)
  },

  /**
   * Get selection stages for a program
   */
  async getSelectionStages(programId: number): Promise<SelectionStage[]> {
    return apiClient.get<SelectionStage[]>(`/api/programs/${programId}/selection-stages/`)
  },

  /**
   * Create a new program
   */
  async create(data: Partial<Program>): Promise<Program> {
    return apiClient.post<Program>('/api/programs/', data)
  },

  /**
   * Update a program
   */
  async update(id: number, data: Partial<Program>): Promise<Program> {
    return apiClient.patch<Program>(`/api/programs/${id}/`, data)
  },

  /**
   * Delete a program
   */
  async delete(id: number): Promise<void> {
    return apiClient.delete(`/api/programs/${id}/`)
  },

  /**
   * Star a program for the current user
   */
  async star(id: number): Promise<{ is_starred: boolean; created: boolean }> {
    return apiClient.post(`/api/programs/${id}/star/`, {})
  },

  /**
   * Unstar a program for the current user
   */
  async unstar(id: number): Promise<{ is_starred: boolean; deleted: boolean }> {
    return apiClient.post(`/api/programs/${id}/unstar/`, {})
  },

  /**
   * Get vendor recommendations for a program (for Kanban board)
   */
  async getVendorRecommendations(programId: number): Promise<{ recommendations: VendorRecommendation[]; count: number }> {
    return apiClient.get(`/api/programs/${programId}/vendor-recommendations/`)
  },

  /**
   * Update the selection stage of a vendor recommendation (Kanban drag-and-drop)
   */
  async updateRecommendationStage(programId: number, recommendationId: number, selectionStageId: number): Promise<any> {
    return apiClient.patch(`/api/programs/${programId}/vendor-recommendations/update-stage/`, {
      recommendation_id: recommendationId,
      selection_stage_id: selectionStageId,
    })
  },
}

// ============================================================================
// Vendors API
// ============================================================================

export const vendorsAPI = {
  /**
   * Get all vendors
   */
  async list(params?: { state?: string; city?: string; search?: string }): Promise<Vendor[]> {
    const queryParams = new URLSearchParams()
    if (params?.state) queryParams.append('state', params.state)
    if (params?.city) queryParams.append('city', params.city)
    if (params?.search) queryParams.append('search', params.search)
    
    const endpoint = `/api/vendors/${queryParams.toString() ? `?${queryParams}` : ''}`
    return apiClient.get<Vendor[]>(endpoint)
  },

  /**
   * Get a specific vendor by ID
   */
  async get(id: number): Promise<Vendor> {
    return apiClient.get<Vendor>(`/api/vendors/${id}/`)
  },

  /**
   * Get submissions for a vendor
   */
  async getSubmissions(id: number): Promise<Submission[]> {
    return apiClient.get<Submission[]>(`/api/vendors/${id}/submissions/`)
  },

  /**
   * Create a new vendor
   */
  async create(data: Partial<Vendor>): Promise<Vendor> {
    return apiClient.post<Vendor>('/api/vendors/', data)
  },

  /**
   * Update a vendor
   */
  async update(id: number, data: Partial<Vendor>): Promise<Vendor> {
    return apiClient.patch<Vendor>(`/api/vendors/${id}/`, data)
  },

  /**
   * Delete a vendor
   */
  async delete(id: number): Promise<void> {
    return apiClient.delete(`/api/vendors/${id}/`)
  },

  /**
   * Get opportunities (ProgramVendorRecommendation records) for a vendor
   */
  async getOpportunities(id: number): Promise<VendorOpportunity[]> {
    return apiClient.get<VendorOpportunity[]>(`/api/vendors/${id}/opportunities/`)
  },
}

// ============================================================================
// Team Members API
// ============================================================================

export const teamMembersAPI = {
  async list(vendorId: number): Promise<TeamMember[]> {
    return apiClient.get<TeamMember[]>(`/api/vendor-teams/?vendor=${vendorId}`)
  },
  async create(data: Partial<TeamMember>): Promise<TeamMember> {
    return apiClient.post<TeamMember>('/api/vendor-teams/', data)
  },
  async update(id: number, data: Partial<TeamMember>): Promise<TeamMember> {
    return apiClient.patch<TeamMember>(`/api/vendor-teams/${id}/`, data)
  },
  async delete(id: number): Promise<void> {
    return apiClient.delete(`/api/vendor-teams/${id}/`)
  },
}

// ============================================================================
// Submissions API
// ============================================================================

export const submissionsAPI = {
  /**
   * Get all submissions (filtered by permissions)
   */
  async list(params?: {
    program?: number
    vendor?: number
    status?: string
    search?: string
  }): Promise<Submission[]> {
    const queryParams = new URLSearchParams()
    if (params?.program) queryParams.append('program', params.program.toString())
    if (params?.vendor) queryParams.append('vendor', params.vendor.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    
    const endpoint = `/api/submissions/${queryParams.toString() ? `?${queryParams}` : ''}`
    return apiClient.get<Submission[]>(endpoint)
  },

  /**
   * Get a specific submission by ID
   */
  async get(id: number): Promise<Submission> {
    return apiClient.get<Submission>(`/api/submissions/${id}/`)
  },

  /**
   * Get evaluations for a submission
   */
  async getEvaluations(id: number): Promise<Evaluation[]> {
    return apiClient.get<Evaluation[]>(`/api/submissions/${id}/evaluations/`)
  },

  /**
   * Create a new submission
   */
  async create(data: Partial<Submission>): Promise<Submission> {
    return apiClient.post<Submission>('/api/submissions/', data)
  },

  /**
   * Update a submission
   */
  async update(id: number, data: Partial<Submission>): Promise<Submission> {
    return apiClient.patch<Submission>(`/api/submissions/${id}/`, data)
  },

  /**
   * Update the selection stage for a submission (for kanban drag-and-drop)
   */
  async updateStage(id: number, selectionStageId: number): Promise<Submission> {
    return apiClient.patch<Submission>(`/api/submissions/${id}/update-stage/`, {
      selection_stage_id: selectionStageId
    })
  },

  /**
   * Assign a reviewer to a submission
   */
  async assignReviewer(
    id: number,
    data: { user_id: number; due_date?: string; notes?: string }
  ): Promise<any> {
    return apiClient.post(`/api/submissions/${id}/assign_reviewer/`, data)
  },

  /**
   * Delete a submission
   */
  async delete(id: number): Promise<void> {
    return apiClient.delete(`/api/submissions/${id}/`)
  },
}

// ============================================================================
// Evaluations API
// ============================================================================

export const evaluationsAPI = {
  /**
   * Get all evaluations (filtered by permissions)
   */
  async list(params?: {
    submission?: number
    evaluator?: number
    status?: string
  }): Promise<Evaluation[]> {
    const queryParams = new URLSearchParams()
    if (params?.submission) queryParams.append('submission', params.submission.toString())
    if (params?.evaluator) queryParams.append('evaluator', params.evaluator.toString())
    if (params?.status) queryParams.append('status', params.status)
    
    const endpoint = `/api/evaluations/${queryParams.toString() ? `?${queryParams}` : ''}`
    return apiClient.get<Evaluation[]>(endpoint)
  },

  /**
   * Get a specific evaluation by ID
   */
  async get(id: number): Promise<Evaluation> {
    return apiClient.get<Evaluation>(`/api/evaluations/${id}/`)
  },

  /**
   * Start an evaluation
   */
  async start(id: number): Promise<Evaluation> {
    return apiClient.post<Evaluation>(`/api/evaluations/${id}/start/`, {})
  },

  /**
   * Complete an evaluation
   */
  async complete(id: number): Promise<Evaluation> {
    return apiClient.post<Evaluation>(`/api/evaluations/${id}/complete/`, {})
  },
}

// ============================================================================
// Technologies API
// ============================================================================

export const technologiesAPI = {
  /**
   * Get all technologies
   */
  async list(params?: { category?: string; parent?: number | 'null' }): Promise<Technology[]> {
    const queryParams = new URLSearchParams()
    if (params?.category) queryParams.append('category', params.category)
    if (params?.parent) queryParams.append('parent', params.parent.toString())
    
    const endpoint = `/api/technologies/${queryParams.toString() ? `?${queryParams}` : ''}`
    return apiClient.get<Technology[]>(endpoint)
  },

  /**
   * Get root-level technologies
   */
  async getRoots(): Promise<Technology[]> {
    return apiClient.get<Technology[]>('/api/technologies/roots/')
  },

  /**
   * Get a specific technology by ID
   */
  async get(id: number): Promise<Technology> {
    return apiClient.get<Technology>(`/api/technologies/${id}/`)
  },

  /**
   * Get technology tree starting from a technology
   */
  async getTree(id: number): Promise<any> {
    return apiClient.get(`/api/technologies/${id}/tree/`)
  },
}

// ============================================================================
// Products API
// ============================================================================

export const productsAPI = {
  async get(id: number): Promise<VendorProduct> {
    return apiClient.get<VendorProduct>(`/api/products/${id}/`)
  },

  async create(data: Partial<VendorProduct>): Promise<VendorProduct> {
    return apiClient.post<VendorProduct>('/api/products/', data)
  },

  async update(id: number, data: Partial<VendorProduct>): Promise<VendorProduct> {
    return apiClient.patch<VendorProduct>(`/api/products/${id}/`, data)
  },

  async uploadImage(productId: number, file: File): Promise<ProductImage> {
    const formData = new FormData()
    formData.append('file', file)
    // Use direct fetch — apiClient.post always sets Content-Type: application/json
    const { tokenManager: tm } = await import('./api')
    const token = tm.getAccessToken()
    const response = await fetch(`/api/products/${productId}/upload-image/`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `Upload failed: ${response.status}`)
    }
    return response.json()
  },

  async deleteImage(imageId: number): Promise<void> {
    return apiClient.delete(`/api/product-images/${imageId}/`)
  },
}

// ============================================================================
// Observations API
// ============================================================================

export const observationsAPI = {
  async list(params?: { product?: number; vendor?: number; spec_type?: string; best_per_spec?: boolean }): Promise<Observation[]> {
    const queryParams = new URLSearchParams()
    if (params?.product) queryParams.append('product', params.product.toString())
    if (params?.vendor) queryParams.append('vendor', params.vendor.toString())
    if (params?.spec_type) queryParams.append('spec_type', params.spec_type)
    if (params?.best_per_spec) queryParams.append('best_per_spec', 'true')
    const endpoint = `/api/observations/${queryParams.toString() ? `?${queryParams}` : ''}`
    return apiClient.get<Observation[]>(endpoint)
  },

  async create(data: Record<string, unknown>): Promise<Observation> {
    return apiClient.post<Observation>('/api/observations/', data)
  },
}

// ============================================================================
// Vendor Documents API
// ============================================================================

export interface VendorDocument {
  id: number
  vendor: number
  products: number[]
  product_names: { id: number; name: string }[]
  document_type: string
  filename: string
  file_size: number
  processing_status: 'uploading' | 'processing' | 'extracting_specs' | 'ready' | 'error'
  processing_error: string
  extracted_specs: Record<string, any>
  observations_created: number
  product_association_results: Record<string, any>
  uploaded_by: number | null
  uploaded_at: string
}

export const vendorDocumentsAPI = {
  async upload(file: File, vendorId: number, documentType: string, productId?: number): Promise<VendorDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('vendor_id', vendorId.toString())
    formData.append('document_type', documentType)
    if (productId) formData.append('product_id', productId.toString())
    const { tokenManager: tm } = await import('./api')
    const token = tm.getAccessToken()
    const response = await fetch('/api/vendor-documents/upload/', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `Upload failed: ${response.status}`)
    }
    return response.json()
  },

  async list(vendorId: number, productId?: number): Promise<VendorDocument[]> {
    const params = new URLSearchParams({ vendor: vendorId.toString() })
    if (productId) params.append('product', productId.toString())
    return apiClient.get<VendorDocument[]>(`/api/vendor-documents/?${params}`)
  },

  async getStatus(documentId: number): Promise<VendorDocument> {
    return apiClient.get<VendorDocument>(`/api/vendor-documents/${documentId}/status/`)
  },

  async delete(documentId: number): Promise<void> {
    return apiClient.delete(`/api/vendor-documents/${documentId}/`)
  },

  async associateProducts(documentId: number, productIds: number[]): Promise<VendorDocument> {
    return apiClient.post<VendorDocument>(`/api/vendor-documents/${documentId}/associate-products/`, { product_ids: productIds })
  },

  pollStatus(documentId: number, onUpdate: (doc: VendorDocument) => void, intervalMs = 3000, maxAttempts = 60): Promise<VendorDocument> {
    return new Promise((resolve, reject) => {
      let attempts = 0
      const poll = async () => {
        try {
          const doc = await vendorDocumentsAPI.getStatus(documentId)
          onUpdate(doc)
          attempts++
          if (doc.processing_status === 'ready' || doc.processing_status === 'error') {
            resolve(doc)
          } else if (attempts >= maxAttempts) {
            resolve(doc)
          } else {
            setTimeout(poll, intervalMs)
          }
        } catch (err) {
          reject(err)
        }
      }
      poll()
    })
  },
}

// ============================================================================
// Export all services
// ============================================================================

export default {
  programs: programsAPI,
  vendors: vendorsAPI,
  submissions: submissionsAPI,
  evaluations: evaluationsAPI,
  technologies: technologiesAPI,
  products: productsAPI,
  observations: observationsAPI,
  vendorDocuments: vendorDocumentsAPI,
}

